import { useTranslation } from "react-i18next";
import { Field, useFormStore } from "@/widgets/classic-form/form";
import type { FormValues } from "@/widgets/classic-form/form";
import { useValidation } from "@/shared/forms";
import type { PrefixObjectAccessor } from "@/shared/forms";
import { useCSSSafeId } from "@/shared/hooks/useCSSSafeId";
import { useTrackSearchFormInteraction } from "@/shared/lib/ube";
import styles from "./YearInputs.module.scss";

const YEAR_INPUT_PATTERN = /^\d{0,4}$/;

export interface YearInputsProps {
    min: number;
    max: number;
    startFieldPath: PrefixObjectAccessor<FormValues, []>;
    endFieldPath: PrefixObjectAccessor<FormValues, []>;
}

/**
 * YearInputs — start year and end year inputs bound to form state via Field.
 * Commits on every change. Validation errors displayed by ui.validator on blur.
 */
export function YearInputs({ min, max, startFieldPath, endFieldPath }: YearInputsProps) {
    const { t } = useTranslation();
    const baseId = useCSSSafeId();
    const startInputId = `${baseId}-start`;
    const endInputId = `${baseId}-end`;
    const trackInteraction = useTrackSearchFormInteraction();

    // Read both year values reactively for cross-field validation — primitives for stable equality
    const startYear = useFormStore(({ values }) => values.timespan.yearStart ?? min);
    const endYear = useFormStore(({ values }) => values.timespan.yearEnd ?? max);

    // Register validation rules — errors display as callout tooltips on blur
    // Enforces at least 1 year gap: start < endYear and end > startYear (integers)
    useValidation(startInputId, {
        errorLocation: "callout",
        rules: {
            year: true,
            greaterThan: { value: min - 1, message: t("timespan.validation.minYear", { min }) },
            lessThan: { value: endYear, message: t("timespan.validation.beforeEnd") },
        },
    });

    useValidation(endInputId, {
        errorLocation: "callout",
        rules: {
            year: true,
            greaterThan: { value: startYear, message: t("timespan.validation.afterStart") },
            lessThan: { value: max + 1, message: t("timespan.validation.maxYear", { max }) },
        },
    });

    return (
        <div className={styles.inputsRow}>
            <div className={styles.inputGroup}>
                <label htmlFor={startInputId} className="textNoWrap">
                    {t("timespan.startYear")}
                </label>
                <Field name={startFieldPath}>
                    {(field) => (
                        <input
                            id={startInputId}
                            type="text"
                            inputMode="numeric"
                            maxLength={4}
                            value={(field.value as number | null) ?? ""}
                            onFocus={() =>
                                trackInteraction(
                                    { action: "focus_start_year", objectName: "estimated_timespan" },
                                    { dedup: true },
                                )
                            }
                            onChange={(e) => {
                                const raw = e.target.value;
                                if (raw === "" || YEAR_INPUT_PATTERN.test(raw)) {
                                    field.onChange(raw === "" ? null : Number(raw));
                                }
                            }}
                            className={styles.yearInput}
                        />
                    )}
                </Field>
            </div>

            <div className={styles.inputDash} aria-hidden="true" />

            <div className={styles.inputGroup}>
                <label htmlFor={endInputId} className="textNoWrap">
                    {t("timespan.endYear")}
                </label>
                <Field name={endFieldPath}>
                    {(field) => (
                        <input
                            id={endInputId}
                            type="text"
                            inputMode="numeric"
                            maxLength={4}
                            value={(field.value as number | null) ?? ""}
                            onFocus={() =>
                                trackInteraction(
                                    { action: "focus_end_year", objectName: "estimated_timespan" },
                                    { dedup: true },
                                )
                            }
                            onChange={(e) => {
                                const raw = e.target.value;
                                if (raw === "" || YEAR_INPUT_PATTERN.test(raw)) {
                                    field.onChange(raw === "" ? null : Number(raw));
                                }
                            }}
                            className={styles.yearInput}
                        />
                    )}
                </Field>
            </div>
        </div>
    );
}
